type Vec3 = [number, number, number];

function normalizeVec3(v: Vec3): Vec3 {
  const len = Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]);
  if (len < 1e-10) {
    return [0, 0, 1];
  }
  return [v[0] / len, v[1] / len, v[2] / len];
}

function angularDistance(a: Vec3, b: Vec3): number {
  const dot = a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
  return Math.acos(Math.max(-1, Math.min(1, dot)));
}

export function computeObservationVector(cameraPosition: Vec3, gcpPosition: Vec3): Vec3 {
  return normalizeVec3([
    cameraPosition[0] - gcpPosition[0],
    cameraPosition[1] - gcpPosition[1],
    cameraPosition[2] - gcpPosition[2],
  ]);
}

/**
 * Selects a diverse subset of estimates by maximizing the angular spread of observation
 * vectors (camera→GCP direction) while respecting confidence scores.
 *
 * Uses greedy farthest-point sampling: each successive pick maximizes a score that
 * balances confidence against the minimum angular gap to already-selected estimates.
 *
 * @param estimates - Pool of candidates to select from.
 * @param getCameraPosition - Returns the 3D camera position for an estimate.
 * @param getGcpPosition - Returns the 3D GCP position for an estimate.
 * @param getConfidence - Returns the confidence score [0, 1] for an estimate.
 * @param limit - Maximum number of estimates to return.
 * @param confidenceWeight - 0 = pure angular diversity, 1 = pure confidence. Default: 0.5.
 * @param preSeededObservationVectors - Already-committed observation vectors (e.g. pinned/accepted
 *   markers) that influence angular scoring but do not count against the limit.
 */
export function selectDiverseEstimates<T>(
  estimates: T[],
  getCameraPosition: (estimate: T) => Vec3,
  getGcpPosition: (estimate: T) => Vec3,
  getConfidence: (estimate: T) => number,
  limit: number,
  confidenceWeight = 0.5,
  preSeededObservationVectors: Vec3[] = [],
): T[] {
  if (estimates.length === 0 || limit <= 0) {
    return [];
  }

  const effectiveLimit = Math.min(limit, estimates.length);
  const vectors: Vec3[] = estimates.map((e) => {
    const cam = getCameraPosition(e);
    const gcp = getGcpPosition(e);
    return normalizeVec3([cam[0] - gcp[0], cam[1] - gcp[1], cam[2] - gcp[2]]);
  });

  const confidences = estimates.map(getConfidence);
  // selectedVectors tracks all committed directions (pre-seeded + newly picked).
  // selectedIndices tracks only the picked indices, used for the limit check and output.
  const selectedVectors: Vec3[] = [...preSeededObservationVectors];
  const selectedIndices: number[] = [];
  const remaining = new Set<number>(estimates.map((_, i) => i));

  // If no angular reference exists yet, seed with the highest-confidence estimate.
  if (selectedVectors.length === 0) {
    let bestIdx = -1;
    let bestConf = -Infinity;
    for (const i of remaining) {
      if (confidences[i] > bestConf) {
        bestConf = confidences[i];
        bestIdx = i;
      }
    }
    selectedVectors.push(vectors[bestIdx]);
    selectedIndices.push(bestIdx);
    remaining.delete(bestIdx);
  }

  // Subsequent picks: score = confidence^w * (minAngularGap / π)^(1-w)
  while (selectedIndices.length < effectiveLimit && remaining.size > 0) {
    let bestScore = -Infinity;
    let bestI = -1;

    for (const i of remaining) {
      let minGap = Infinity;
      for (const sv of selectedVectors) {
        const gap = angularDistance(vectors[i], sv);
        if (gap < minGap) {
          minGap = gap;
        }
      }

      const score =
        Math.pow(confidences[i], confidenceWeight) *
        Math.pow(minGap / Math.PI, 1 - confidenceWeight);

      if (score > bestScore) {
        bestScore = score;
        bestI = i;
      }
    }

    selectedVectors.push(vectors[bestI]);
    selectedIndices.push(bestI);
    remaining.delete(bestI);
  }

  return selectedIndices.map((i) => estimates[i]);
}
